import React, { useState, useEffect, useCallback, useMemo } from 'react';
import intl from 'react-intl-universal';
import isEmpty from 'lodash/isEmpty';
import {
  Typography,
  FormItem,
  Dropdown,
  TextField,
} from '@getsynapse/design-system';
import { PROJECT_ONHOLD_REASONS } from 'utils/constants';
import useModal from 'Hooks/useModal';
import classNames from 'classnames';

type Option = {
  label: string;
  value: string;
};

export const SpecifyHoldReasonForm: React.FC<{
  holdReason?: string;
  setHoldReason: React.Dispatch<React.SetStateAction<string>>;
  otherReason?: string;
  setOtherReason: React.Dispatch<React.SetStateAction<string>>;
  shouldSpecifyOtherReason?: boolean;
  canUpdateHoldReason?: boolean;
  sidePanel?: boolean;
}> = ({
  holdReason,
  setHoldReason,
  otherReason,
  setOtherReason,
  shouldSpecifyOtherReason = false,
  canUpdateHoldReason = true,
  sidePanel = false,
}) => {
  const reasonOptions = useMemo(
    () =>
      Object.entries(PROJECT_ONHOLD_REASONS).map((entry) => {
        return {
          label: intl.get(
            `PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.REASON_PICKER_OPTIONS.${entry[0]}`
          ),
          value: entry[1],
        };
      }),
    []
  );
  const selectedReason = useMemo(
    () =>
      isEmpty(holdReason)
        ? []
        : reasonOptions.filter((reason: Option) => reason.value === holdReason),
    [holdReason, reasonOptions]
  );

  return (
    <React.Fragment>
      <FormItem
        label={intl.get(
          'PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.REASONS_PICKER_LABEL'
        )}
        className={classNames(`${!sidePanel ? 'mt-2' : 'mb-4'}`)}
        labelProps={{
          required: true,
        }}
      >
        {canUpdateHoldReason ? (
          <Dropdown
            options={reasonOptions}
            onChange={(option: Option) => setHoldReason(option.value)}
            values={selectedReason}
            placeholder={intl.get(
              'PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.REASONS_PICKER_PLACEHOLDER'
            )}
            triggerProps={{
              'data-cy': 'hold-reason-picker',
            }}
          />
        ) : (
          <Typography variant='body2'>{selectedReason[0]?.label}</Typography>
        )}
      </FormItem>
      {shouldSpecifyOtherReason && (
        <FormItem
          label={intl.get(
            'PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.PLEASE_SPECIFY'
          )}
          className={classNames(`${!sidePanel ? 'mt-6' : 'mb-4'}`)}
          labelProps={{
            required: true,
          }}
        >
          {canUpdateHoldReason ? (
            <TextField
              value={otherReason}
              className='p-1'
              onChange={(event: React.KeyboardEvent<HTMLInputElement>) => {
                const target = event.target as HTMLInputElement;
                setOtherReason(target.value);
              }}
              data-cy='specify-hold-reason-input'
              placeholder={intl.get(
                'PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.SPECIFY_REASON_PLACEHOLDER'
              )}
            />
          ) : (
            <Typography variant='body2'>{otherReason}</Typography>
          )}
        </FormItem>
      )}
    </React.Fragment>
  );
};

const PutProjectOnHoldModal: React.FC<{
  onConfirm: (onHoldReason: string) => void;
  holdReason?: string;
  shouldDisplayModal?: boolean;
  isReadOnly?: boolean;
  canUpdateHoldReason?: boolean;
  onClose: () => void;
  onCancelCallback: () => void;
}> = ({
  onConfirm,
  holdReason,
  shouldDisplayModal = false,
  isReadOnly = false,
  canUpdateHoldReason = true,
  onClose,
  onCancelCallback,
}) => {
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const { Modal, modalProps, openModal, closeModal, updateModal } = useModal();
  const shouldSpecifyOtherReason =
    reason ===
    intl.get(
      'PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.REASON_PICKER_OPTIONS.OTHER'
    );
  const canPutProjectOnHold =
    (!isEmpty(reason) && !shouldSpecifyOtherReason) ||
    (!isEmpty(reason) && shouldSpecifyOtherReason && !isEmpty(otherReason));

  useEffect(() => {
    if (!shouldSpecifyOtherReason && !isEmpty(otherReason)) {
      setOtherReason('');
    }
  }, [otherReason, shouldSpecifyOtherReason]);

  useEffect(() => {
    if (modalProps.isOpen && !isEmpty(holdReason)) {
      if (holdReason?.includes(':')) {
        const colonIndex = holdReason.indexOf(':');
        setReason(holdReason.slice(0, colonIndex));
        setOtherReason(holdReason.slice(colonIndex + 1));
      } else {
        setReason(holdReason!);
      }
    }
  }, [holdReason, setReason, setOtherReason, modalProps.isOpen]);

  const handleCloseModal = useCallback(() => {
    setReason('');
    setOtherReason('');
    closeModal();
    onClose();
  }, [setReason, setOtherReason, closeModal, onClose]);

  const handleConfirmClick = useCallback(() => {
    onConfirm(
      `${shouldSpecifyOtherReason ? `${reason}:${otherReason}` : `${reason}`}`
    );
    handleCloseModal();
  }, [
    onConfirm,
    reason,
    otherReason,
    shouldSpecifyOtherReason,
    handleCloseModal,
  ]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.CONFIRM'),
        disabled: !canPutProjectOnHold || !canUpdateHoldReason || isReadOnly,
        onClick: handleConfirmClick,
        'data-cy': 'confirm-button',
      },
      {
        children: intl.get('PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.CANCEL'),
        variant: 'secondary',
        'data-cy': 'cancel-button',
        onClick: () => {
          onCancelCallback();
          handleCloseModal();
        },
      },
    ],
    [
      handleCloseModal,
      handleConfirmClick,
      canPutProjectOnHold,
      canUpdateHoldReason,
      isReadOnly,
      onCancelCallback,
    ]
  );

  useEffect(() => {
    if (modalProps.isOpen) {
      updateModal({
        children: (
          <SpecifyHoldReasonForm
            holdReason={reason}
            otherReason={otherReason}
            setHoldReason={setReason}
            setOtherReason={setOtherReason}
            shouldSpecifyOtherReason={shouldSpecifyOtherReason}
            canUpdateHoldReason={canUpdateHoldReason && !isReadOnly}
          />
        ),
        actionButtons,
      });
    }
  }, [
    reason,
    otherReason,
    shouldSpecifyOtherReason,
    modalProps.isOpen,
    updateModal,
    canUpdateHoldReason,
    isReadOnly,
    actionButtons,
  ]);

  const displayPutProjectOnHoldModal = useCallback(() => {
    openModal({
      title: intl.get('PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.TITLE'),
      size: 'medium',
      children: (
        <SpecifyHoldReasonForm
          holdReason={reason}
          otherReason={otherReason}
          setHoldReason={setReason}
          setOtherReason={setOtherReason}
          shouldSpecifyOtherReason={shouldSpecifyOtherReason}
          canUpdateHoldReason={canUpdateHoldReason && !isReadOnly}
        />
      ),
      actionButtons,
    });
  }, [
    openModal,
    canUpdateHoldReason,
    isReadOnly,
    reason,
    otherReason,
    shouldSpecifyOtherReason,
    actionButtons,
  ]);

  useEffect(() => {
    if (shouldDisplayModal && !modalProps.isOpen) {
      displayPutProjectOnHoldModal();
    }
  }, [shouldDisplayModal, displayPutProjectOnHoldModal, modalProps.isOpen]);

  return (
    <Modal
      {...modalProps}
      aria-label={intl.get('PROJECT_DETAIL.PUT_PROJECT_ON_HOLD_MODAL.TITLE')}
      closeModal={handleCloseModal}
      data-cy='put-project-on-hold-modal'
    />
  );
};

export default PutProjectOnHoldModal;
